import { forwardRef, useState, useCallback, useRef, useImperativeHandle, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { HiX } from 'react-icons/hi';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';

type BarcodeScannerModalProps = {
  onScanned: (value: string) => void;
};

const READER_ID = 'barcode-scanner-reader';

const isScannerRunning = (scanner: Html5Qrcode): boolean => {
  try {
    const state = scanner.getState();

    return state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED;
  } catch {
    return false;
  }
};

const safeStopAndClear = async (scanner: Html5Qrcode) => {
  try {
    if (isScannerRunning(scanner)) {
      await scanner.stop();
    }
  } catch {
    // already stopped or never started
  }

  try {
    scanner.clear();
  } catch {
    // DOM element may already be gone
  }
};

const BarcodeScannerModal = forwardRef<ModalMethods, BarcodeScannerModalProps>(
  ({ onScanned }, ref) => {
    const [scannedValue, setScannedValue] = useState<string | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [manualMode, setManualMode] = useState(false);
    const [manualInput, setManualInput] = useState('');
    const [isVisible, setIsVisible] = useState(false);

    const internalModalRef = useRef<ModalMethods>(null);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;

        if (scannerRef.current) {
          safeStopAndClear(scannerRef.current);
          scannerRef.current = null;
        }
      };
    }, []);

    const stopScanner = useCallback(() => {
      if (scannerRef.current) {
        safeStopAndClear(scannerRef.current);
        scannerRef.current = null;
      }

      setIsScanning(false);
    }, []);

    const startScanner = useCallback(async () => {
      setScannedValue(null);
      setError(null);
      setManualMode(false);
      setManualInput('');

      const readerEl = document.getElementById(READER_ID);

      if (!readerEl) {
        setError('Scanner container not ready. Please try again.');

        return;
      }

      if (scannerRef.current) {
        await safeStopAndClear(scannerRef.current);
        scannerRef.current = null;
      }

      readerEl.innerHTML = '';

      try {
        setIsScanning(true);
        const scanner = new Html5Qrcode(READER_ID);

        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!isMountedRef.current) {
              return;
            }

            setScannedValue(decodedText);

            if (isScannerRunning(scanner)) {
              scanner.stop().catch(() => {});
            }

            setIsScanning(false);
          },
          () => {}
        );
      } catch {
        if (!isMountedRef.current) {
          return;
        }

        setIsScanning(false);
        setManualMode(true);
        setError('Camera not available. You can enter the code manually below.');
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        show: () => {
          internalModalRef.current?.show();
          setScannedValue(null);
          setError(null);
          setManualMode(false);
          setManualInput('');
          setIsVisible(true);

          setTimeout(startScanner, 400);
        },
        hide: () => {
          stopScanner();
          setIsVisible(false);
          internalModalRef.current?.hide();
        },
        toggle: () => {
          internalModalRef.current?.toggle();
        },
        getModalState: () => internalModalRef.current?.getModalState() ?? false,
      }),
      [startScanner, stopScanner]
    );

    const handleConfirm = useCallback(
      (value: string) => {
        if (!value.trim()) {
          toasts('error', 'Scanned value is empty', 'scanner-empty');

          return;
        }

        onScanned(value.trim());
        stopScanner();
        setIsVisible(false);
        internalModalRef.current?.hide();
      },
      [onScanned, stopScanner]
    );

    const handleRetry = useCallback(() => {
      setScannedValue(null);
      setError(null);
      startScanner();
    }, [startScanner]);

    const handleClose = useCallback(() => {
      stopScanner();
      setIsVisible(false);
      internalModalRef.current?.hide();
    }, [stopScanner]);

    const handleSwitchToManual = useCallback(() => {
      stopScanner();
      setManualMode(true);
      setError(null);
    }, [stopScanner]);

    const handleSwitchToCamera = useCallback(() => {
      setManualMode(false);
      setManualInput('');
      setTimeout(startScanner, 200);
    }, [startScanner]);

    const headerComponent = (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <HiX size={20} />
        </button>
        <span className="font-inter font-semibold text-base text-gray-900">Scan QR / Barcode</span>
      </div>
    );

    return (
      <Modal
        ref={internalModalRef}
        size="medium"
        headerComponent={headerComponent}
        showCloseIcon={false}
        showFooter={false}
      >
        <div className="flex flex-col items-center py-4 h-full">
          {scannedValue ? (
            <div className="flex flex-col items-center justify-center flex-1 w-full px-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#22c55e"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="font-inter font-semibold text-sm text-gray-900 mb-1">Code Detected!</p>
              <div className="w-full max-w-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-6 text-center">
                <p className="font-inter text-sm text-gray-800 font-medium break-all">
                  {scannedValue}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleRetry}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-inter font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Scan Again
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirm(scannedValue)}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-inter font-semibold hover:bg-blue-700 transition-colors"
                >
                  Use This Code
                </button>
              </div>
            </div>
          ) : manualMode ? (
            <div className="flex flex-col items-center justify-center flex-1 w-full px-4">
              {error && (
                <div className="w-full max-w-xs bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4 text-center">
                  <p className="font-inter text-xs text-amber-700">{error}</p>
                </div>
              )}
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="14"
                    rx="2"
                    stroke="#666"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <line
                    x1="7"
                    y1="12"
                    x2="17"
                    y2="12"
                    stroke="#666"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="font-inter font-semibold text-sm text-gray-900 mb-4">
                Enter code manually
              </p>
              <div className="w-full max-w-xs mb-4">
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualInput.trim()) {
                      handleConfirm(manualInput);
                    }
                  }}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-inter text-gray-800 outline-none focus:border-blue-500 transition-colors"
                  placeholder="Enter or paste barcode / QR value"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSwitchToCamera}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-inter font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Use Camera
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirm(manualInput)}
                  disabled={!manualInput.trim()}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-inter font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Confirm
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center flex-1 w-full">
              <div
                className="relative w-full max-w-sm overflow-hidden rounded-xl bg-black"
                style={{ minHeight: 280 }}
              >
                {isVisible && <div id={READER_ID} className="w-full [&>video]:rounded-xl" />}

                {isScanning && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-[200px] h-[200px] relative">
                      <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl" />
                      <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr" />
                      <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl" />
                      <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br" />
                      <span className="absolute left-2 right-2 top-1/2 h-0.5 bg-red-500/70 animate-pulse" />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 px-4">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mb-3">
                      <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.5" />
                      <path
                        d="M12 8v4m0 4h.01"
                        stroke="#ef4444"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p className="text-white text-sm font-inter text-center mb-3">{error}</p>
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="px-4 py-1.5 border border-white/30 rounded-lg text-xs text-white font-inter hover:bg-white/10 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>

              <p className="font-inter text-xs text-gray-500 mt-3 text-center">
                {isScanning
                  ? 'Point your camera at a QR code or barcode'
                  : 'Initializing camera...'}
              </p>

              <button
                type="button"
                onClick={handleSwitchToManual}
                className="mt-4 font-inter text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Enter code manually instead
              </button>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

BarcodeScannerModal.displayName = 'BarcodeScannerModal';

export default BarcodeScannerModal;
